import axios from 'axios';
import history from '../historyData';
import { ROUTES, TOKEN } from './constants';
import {
  getIsRefreshing,
  getPendingRefreshPromise,
  isTokenExpired,
  refreshTokens,
} from './tokenManager';

// use only for image upload
const api = axios.create({
  baseURL: process.env.REACT_APP_SERVER_REST_URL,
});

// Request interceptor - auto-attach auth headers and proactively refresh expired tokens
api.interceptors.request.use(async (config) => {
  // If a token refresh is in progress, wait for it to complete before sending
  if (getIsRefreshing() && getPendingRefreshPromise()) {
    await getPendingRefreshPromise().catch(() => {});
  }

  // eslint-disable-next-line no-undef
  let userToken = localStorage.getItem(TOKEN);

  // Proactively refresh if token is expired before sending the request
  if (userToken && isTokenExpired(userToken)) {
    try {
      userToken = await refreshTokens();
    } catch {
      // Refresh failed, continue with expired token (response interceptor will handle it)
    }
  }

  // eslint-disable-next-line no-param-reassign
  config.headers.Authorization = userToken ? `Bearer ${userToken}` : '';

  return config;
});

// Response interceptor - refresh and retry once on a refreshable auth error
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const statusCode = error?.response?.status;
    const apiErrorMessage =
      error?.response?.data?.error?.message || error?.response?.data?.message;

    // Determine if this is a refreshable auth error:
    // - 405 with 'jwt expired' message (access token expired)
    // - 401 (unauthorized - access token may be expired)
    // Non-refreshable 405 errors (session expired, password reset) go straight to logout
    const isJwtExpired = apiErrorMessage === 'jwt expired';
    const isRefreshable =
      (isJwtExpired || statusCode === 401) &&
      originalRequest &&
      !originalRequest?.retried;

    if (isRefreshable) {
      // eslint-disable-next-line no-param-reassign
      originalRequest.retried = true;
      // eslint-disable-next-line no-undef
      const existingToken = localStorage.getItem(TOKEN);

      if (existingToken) {
        try {
          const newToken = await refreshTokens();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch {
          // Refresh failed (refresh token likely expired - server returns 401)
          history.replace(ROUTES?.LOGOUT, {
            // eslint-disable-next-line no-undef
            pathname: window?.location?.pathname,
          });
          return Promise.reject(error);
        }
      }
    }

    // Handle non-refreshable 405 errors (session expired, password reset, etc.) - logout directly
    if (statusCode === 405 && !isJwtExpired) {
      history.replace(ROUTES?.LOGOUT, {
        // eslint-disable-next-line no-undef
        pathname: window?.location?.pathname,
      });
    }

    return Promise.reject(error);
  },
);

export default api;
